import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Image } from "@mantine/core";
import { IconLogout, IconUser, IconSettings } from "@tabler/icons-react";
import { useAuth } from "../../hooks/useAuth";
import { NotificationBell } from "../NotificationBell/NotificationBell";
import { CurrencySelect } from "./CurrencySelect";
import { ConsolidatedToggle } from "./ConsolidatedToggle";
import { OrgSwitcher } from "./OrgSwitcher";

interface AppHeaderProps {
  onLogoutClick: () => void;
  onNavigateHome: () => void;
}

export const AppHeader = ({
  onLogoutClick,
  onNavigateHome,
}: AppHeaderProps) => {
  const { email, scopes } = useAuth();
  const canOpenSettings = scopes?.includes("SETTINGS") ?? false;
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleSettingsClick = () => {
    navigate("/settings");
    setIsUserMenuOpen(false);
  };

  const handleLogoutClick = () => {
    setIsUserMenuOpen(false);
    onLogoutClick();
  };

  return (
    <div className="flex items-center justify-between w-full h-full px-4 bg-white border-b border-gray-200">
      <div
        className="flex items-center cursor-pointer transition-opacity hover:opacity-80"
        onClick={onNavigateHome}
      >
        <Image className="h-10" src="/logo.jpeg" alt="Datamint Logo" />
      </div>

      <div className="flex items-center gap-2 mr-3">
        <OrgSwitcher />
        <ConsolidatedToggle />
        <CurrencySelect />
        <NotificationBell />

        <div className="relative" ref={userMenuRef}>
          <ActionIcon
            variant="light"
            size="lg"
            radius="md"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="transition-all hover:bg-gray-100"
          >
            <IconUser size={20} stroke={1.5} />
          </ActionIcon>

          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {email || "User"}
                </p>
              </div>

              <div className="py-1">
                {canOpenSettings ? (
                  <button
                    onClick={handleSettingsClick}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <IconSettings size={18} stroke={1.5} />
                    <span>Settings</span>
                  </button>
                ) : null}
                <button
                  onClick={handleLogoutClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <IconLogout size={18} stroke={1.5} />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
